import React, { ReactNode, useState } from 'react'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import type { PAMResource } from '../api/types'
import type {
  AdminCreateResourceReq,
  AdminResourceItem,
  AdminUpdateResourceReq,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

const NATIVE_OPTIONS = [
  'ssh',
  'rdp',
  'db',
  'k8s',
  'cloud_role',
  'http_api',
  'saas_admin',
].map((v) => ({ value: v, label: v }))

const ENV_OPTIONS = ['dev', 'staging', 'prod'].map((v) => ({
  value: v,
  label: v,
}))

const SENS_OPTIONS = ['low', 'medium', 'high', 'critical'].map((v) => ({
  value: v,
  label: v,
}))

const columns: CrudColumn<PAMResource>[] = [
  { key: 'name', header: 'Name' },
  { key: 'native_type', header: 'Type' },
  { key: 'environment', header: 'Env', render: (r) => r.environment ?? '-' },
  {
    key: 'sensitivity',
    header: 'Sensitivity',
    render: (r) => r.sensitivity ?? '-',
  },
  { key: 'owner', header: 'Owner', render: (r) => r.owner ?? '-' },
]

const fields: CrudField[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  {
    name: 'native_type',
    label: 'Native Type',
    type: 'select',
    options: NATIVE_OPTIONS,
    required: true,
  },
  {
    name: 'environment',
    label: 'Environment',
    type: 'select',
    options: ENV_OPTIONS,
  },
  {
    name: 'sensitivity',
    label: 'Sensitivity',
    type: 'select',
    options: SENS_OPTIONS,
  },
  { name: 'owner', label: 'Owner (subject id)', type: 'text' },
  { name: 'approval_policy_ref', label: 'Approval Policy Ref', type: 'text' },
  { name: 'metadata', label: 'Metadata (JSON, write-only)', type: 'textarea' },
]

const CREATE_INITIAL: Record<string, unknown> = {
  native_type: 'db',
  environment: 'dev',
  sensitivity: 'low',
}

/**
 * Map the generated AdminResourceItem (camelCase) → PAMResource (snake_case)
 * so all existing column/form/edit logic stays unchanged. Metadata is encrypted
 * at rest and NEVER returned by the API, so it is absent here (write-only).
 *
 * Imports the REAL generated `AdminResourceItem` type (rather than a
 * page-local structural stand-in) so a future codegen field rename fails
 * typecheck here instead of silently rendering blank — same defensive
 * posture the Gate-6 shared constraints doc calls out for the pilot's
 * StepUpPolicy fix (`satisfies`/real-type mapping over ad-hoc casts).
 */
function toPAMResource(r: AdminResourceItem): PAMResource {
  return {
    id: r.id,
    tenant_id: r.tenantId,
    name: r.name,
    native_type: r.nativeType,
    owner: r.owner,
    environment: r.environment,
    sensitivity: r.sensitivity,
    approval_policy_ref: r.approvalPolicyRef,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
  }
}

const toCreateReq = (
  tenantId: string,
  v: Record<string, unknown>
): AdminCreateResourceReq => ({
  tenantId,
  name: String(v.name ?? ''),
  nativeType: String(v.native_type || 'db'),
  environment: v.environment ? String(v.environment) : undefined,
  sensitivity: v.sensitivity ? String(v.sensitivity) : undefined,
  owner: v.owner ? String(v.owner) : undefined,
  approvalPolicyRef: v.approval_policy_ref
    ? String(v.approval_policy_ref)
    : undefined,
  metadata: v.metadata ? String(v.metadata) : undefined,
})

const toUpdateReq = (
  tenantId: string,
  resourceId: string,
  v: Record<string, unknown>
): AdminUpdateResourceReq => ({
  tenantId,
  resourceId,
  name: v.name ? String(v.name) : undefined,
  nativeType: v.native_type ? String(v.native_type) : undefined,
  environment: v.environment ? String(v.environment) : undefined,
  sensitivity: v.sensitivity ? String(v.sensitivity) : undefined,
  owner: v.owner ? String(v.owner) : undefined,
  approvalPolicyRef: v.approval_policy_ref
    ? String(v.approval_policy_ref)
    : undefined,
  metadata: v.metadata ? String(v.metadata) : undefined,
})

export const Resources = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [editing, setEditing] = useState<PAMResource | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PAMResource | null>(null)

  const rs = useResource<PAMResource>({
    list: async () => {
      const resp = await client.adminListResources({ tenantId: tenant })
      return (resp.resources ?? []).map(toPAMResource)
    },
    remove: async (id) => {
      await client.adminDeleteResource({ tenantId: tenant, resourceId: id })
    },
  })

  const handleCreate = async (v: Record<string, unknown>): Promise<void> => {
    try {
      await client.adminCreateResource(toCreateReq(tenant, v))
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleEdit = async (v: Record<string, unknown>): Promise<void> => {
    if (!editing) return
    try {
      await client.adminUpdateResource(toUpdateReq(tenant, editing.id, v))
      await rs.reload()
      setEditing(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (r: PAMResource): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditing(r)
          setActionError(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(r)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>
          Please select a tenant to manage resources.
        </TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>Resources</PageTitle>

      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}

      <SectionBlock>
        <SectionTitle>All Resources</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Resources list"
          emptyMessage="No resources configured yet."
        />
      </SectionBlock>

      {editing && (
        <SectionBlock>
          <SectionTitle>Edit Resource</SectionTitle>
          <CrudForm
            key={editing.id}
            fields={fields}
            initial={{
              name: editing.name,
              native_type: editing.native_type,
              environment: editing.environment ?? '',
              sensitivity: editing.sensitivity ?? '',
              owner: editing.owner ?? '',
              approval_policy_ref: editing.approval_policy_ref ?? '',
              metadata: '',
            }}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditing(null)
              setActionError(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionTitle>Create Resource</SectionTitle>
        <CrudForm
          fields={fields}
          initial={CREATE_INITIAL}
          onSubmit={handleCreate}
          submitLabel="Create Resource"
        />
      </SectionBlock>

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete resource"
        body={`Delete resource "${
          pendingDelete?.name ?? ''
        }"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
